import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router'
import { observer } from 'mobx-react-lite'
import { useStores } from '@/components/StoreProvider'
import { Menu, X, LayoutDashboard, GraduationCap } from 'lucide-react'
import Home from '../../images/home.svg?react'
import History from '../../images/history.svg?react'
import Settings from '../../images/settings.svg?react'
import User from '../../images/user.svg?react'

const MainHeader: React.FC = observer(() => {
  const navigate = useNavigate()
  const location = useLocation()
  const { userStore } = useStores()
  const { user } = userStore
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)

  if (!user) return null

  // Close menu when route changes
  useEffect(() => {
    handleCloseMenu()
  }, [location.pathname])

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMobileMenuOpen])

  const handleCloseMenu = () => {
    setIsClosing(true)
    setTimeout(() => {
      setIsMobileMenuOpen(false)
      setIsClosing(false)
    }, 300) // Match animation duration
  }

  const isActive = (route: string) => location.pathname === route

  const baseMenuItems = [
    { route: '/', icon: Home, label: 'Home', ariaLabel: 'Home' },
    { route: '/history', icon: History, label: 'History', ariaLabel: 'History' },
    { route: '/user', icon: User, label: 'Profile', ariaLabel: 'User Profile' },
    { route: '/settings', icon: Settings, label: 'Settings', ariaLabel: 'Settings' },
  ]

  // Add coach and dashboard buttons based on user roles
  const menuItems = [
    ...baseMenuItems,
    ...(userStore.isCoach ? [{ route: '/coach', icon: () => <GraduationCap style={{ width: 24, height: 24 }} />, label: 'Coach', ariaLabel: 'Coach Dashboard' }] : []),
    ...(userStore.isAdmin ? [{ route: '/dashboard', icon: () => <LayoutDashboard style={{ width: 24, height: 24 }} />, label: 'Dashboard', ariaLabel: 'Dashboard' }] : []),
  ]

  const handleNavigate = (route: string) => {
    navigate(route)
    handleCloseMenu()
  }

  return (
    <>
      <header className="font-[Sora] fixed top-0 left-0 right-0 bg-gradient-to-r from-[#004D43] to-[#193133] shadow-lg z-20">
        <div className="mx-auto px-4 py-3">
          <div className="flex justify-between items-center text-teal-50">
            <div className="flex items-center gap-3">
              {/* Burger Menu Button - Mobile Only */}
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden p-2 rounded-lg hover:bg-white/10 transition-colors duration-200" aria-label="Toggle menu">
                <Menu className="w-6 h-6" />
              </button>

              <button onClick={() => navigate('/')} className="text-xl md:text-2xl font-bold text-teal-50 hover:opacity-80 transition-opacity duration-200 cursor-pointer" aria-label="Go to homepage">
                <img src="/g.svg" alt="Logo" className="h-7 md:h-8" />
              </button>
            </div>

            {/* Desktop Navigation - Hidden on Mobile */}
            <nav className="hidden md:flex space-x-3">
              {menuItems.map((item) => {
                const Icon = item.icon
                return (
                  <button
                    key={item.route}
                    onClick={() => handleNavigate(item.route)}
                    className={`p-2 rounded-full transition duration-200 cursor-pointer ${isActive(item.route) ? 'bg-[#E3FFAB33]' : 'hover:bg-[#E3FFAB33]'}`}
                    aria-label={item.ariaLabel}
                  >
                    <Icon style={{ width: 26, height: 24 }} />
                  </button>
                )
              })}
            </nav>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop with blur */}
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 md:hidden transition-opacity duration-300"
            onClick={handleCloseMenu}
            style={{
              animation: isClosing ? 'fadeOut 0.3s ease-out' : 'fadeIn 0.3s ease-out',
              opacity: isClosing ? 0 : 1,
            }}
          />

          {/* Sliding Menu Panel */}
          <nav
            className="fixed top-0 left-0 h-full w-72 bg-gradient-to-b from-[#004D43] to-[#193133] shadow-2xl z-40 md:hidden overflow-y-auto"
            style={{
              animation: isClosing ? 'slideOutToLeft 0.3s ease-out' : 'slideInFromLeft 0.3s ease-out',
              transform: isClosing ? 'translateX(-100%)' : 'translateX(0)',
            }}
          >
            <div className="flex flex-col h-full">
              {/* Menu Header */}
              <div className="flex items-center justify-between p-4 border-b border-white/10">
                <button onClick={() => handleNavigate('/')} className="flex items-center gap-3 hover:opacity-80 transition-opacity duration-200 cursor-pointer" aria-label="Go to homepage">
                  <img src="/g.svg" alt="Logo" className="h-8" />
                </button>
                <button onClick={handleCloseMenu} className="p-2 rounded-lg hover:bg-white/10 transition-colors duration-200 text-teal-50" aria-label="Close menu">
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Menu Items */}
              <div className="flex flex-col p-2 gap-1 mt-4">
                {menuItems.map((item) => {
                  const Icon = item.icon
                  const active = isActive(item.route)
                  return (
                    <button
                      key={item.route}
                      onClick={() => handleNavigate(item.route)}
                      className={`flex items-center gap-4 px-4 py-3 rounded-lg transition-all duration-200 ${active ? 'bg-[#E3FFAB]/20 text-[#E3FFAB] shadow-md' : 'text-teal-50 hover:bg-white/10'}`}
                    >
                      <Icon style={{ width: 24, height: 24 }} />
                      <span className="text-base font-medium">{item.label}</span>
                    </button>
                  )
                })}
              </div>

              {/* Footer Info */}
              <div className="mt-auto p-4 border-t border-white/10">
                <p className="text-teal-200 text-xs text-center">{user.email}</p>
              </div>
            </div>
          </nav>
        </>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes slideInFromLeft {
          from {
            transform: translateX(-100%);
          }
          to {
            transform: translateX(0);
          }
        }
        
        @keyframes slideOutToLeft {
          from {
            transform: translateX(0);
          }
          to {
            transform: translateX(-100%);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes fadeOut {
          from {
            opacity: 1;
          }
          to {
            opacity: 0;
          }
        }
      `}</style>
    </>
  )
})

export default MainHeader
